// API identity plumbing.
//
// The server reads the acting user from an `X-User-Id` header (dev stub
// until Cognito lands). Rather than thread that header through every
// fetch callsite, we patch window.fetch ONCE to add it to same-origin
// /api/* requests. deck.gl tile-content fetches get it too — harmless,
// and means tile loads resolve as the right user when we later enforce
// per-asset visibility.
//
// The acting user id lives in localStorage so the patch can read it
// synchronously without importing the (async) zustand store, avoiding a
// circular dependency.

import {
  ACTING_USER_HEADER, ACTING_USER_STORAGE_KEY,
  ACTING_CUSTOMER_HEADER, ACTING_CUSTOMER_STORAGE_KEY,
} from '@twin/contracts/identity';

const STORAGE_KEY = ACTING_USER_STORAGE_KEY;
const TOKEN_KEY = 'twin-auth-token';

export function getActingUserId() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function setActingUserId(id) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// v22: the active-customer switcher (multi-customer membership). Persisted like
// the acting-user id and attached as ACTING_CUSTOMER_HEADER on /api/* requests.
export function getActiveCustomerId() {
  try {
    return localStorage.getItem(ACTING_CUSTOMER_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function setActiveCustomerId(id) {
  try {
    if (id) localStorage.setItem(ACTING_CUSTOMER_STORAGE_KEY, id);
    else localStorage.removeItem(ACTING_CUSTOMER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setAuthToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

// Identity headers for API transports that cannot use the global fetch
// interceptor (notably XMLHttpRequest upload progress). Keep this in one place
// so fetch and XHR resolve the same acting user and active customer.
export function getApiIdentityHeaders() {
  const headers = {};
  const actingId = getActingUserId();
  const token = getAuthToken();
  if (actingId) headers[ACTING_USER_HEADER] = actingId;
  else if (token) headers.Authorization = `Bearer ${token}`;

  const activeCustomer = getActiveCustomerId();
  if (activeCustomer) headers[ACTING_CUSTOMER_HEADER] = activeCustomer;
  return headers;
}

let installed = false;

export function installApiClient() {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;
  const orig = window.fetch.bind(window);

  window.fetch = (input, init = {}) => {
    let url = '';
    try {
      url = typeof input === 'string' ? input : (input?.url || '');
    } catch {
      url = '';
    }
    // Only decorate same-origin /api/* requests.
    const isApi = url.startsWith('/api/') ||
      (typeof location !== 'undefined' && url.startsWith(`${location.origin}/api/`));
    if (!isApi) return orig(input, init);

    // Precedence: a dev impersonation override (X-User-Id) wins — it lets
    // the switcher act as any user. Otherwise attach the real session
    // token as Bearer. The server honours Bearer first, then X-User-Id,
    // so suppressing the token during impersonation keeps the override
    // authoritative.
    const identityHeaders = getApiIdentityHeaders();
    if (!Object.keys(identityHeaders).length) return orig(input, init);
    const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined) || {});
    for (const [key, value] of Object.entries(identityHeaders)) {
      if (!headers.has(key)) headers.set(key, value);
    }
    return orig(input, { ...init, headers });
  };
}
